import { useState } from 'react'

import type {
  ButtonsInstanceProps,
  PayPalApproveActions,
  PayPalData,
  PayPalInitActions,
} from '@fourthwall/components'
import { getCssVariable } from '@fourthwall/components'

import { PAYPAL_CONTAINER_ID } from '../consts'
import { loadPayPalScript } from '../utils/loadPayPalScript'

type RenderButtonProps = {
  vaultSetupToken: string
  onApprove: (data: PayPalData, actions: PayPalApproveActions) => Promise<void>
  onCancel?: () => void
  onClick?: () => void
  onError?: (error: Error) => void
}

export const usePayPal = () => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [isRendered, setIsRendered] = useState(false)
  const [button, setButton] = useState<ButtonsInstanceProps>()
  const [payPalActions, setPayPalActions] = useState<PayPalInitActions>()

  const initialize = async (apiKey: string, userTokenId: string) => {
    await loadPayPalScript(apiKey, userTokenId)
    setIsInitialized(true)
  }

  const renderButton = ({
    vaultSetupToken,
    onApprove,
    onCancel,
    onClick,
    onError,
  }: RenderButtonProps) => {
    if (!isInitialized) {
      return
    }

    const borderRadius = getCssVariable('--radius-brand').replace('px', '')
    const buttonsInstance = window.paypal.Buttons({
      locale: 'en_US',
      style: {
        color: 'gold',
        size: 'medium',
        shape: 'sharp',
        label: 'paypal',
        height: 48,
        borderRadius: borderRadius ? Number(borderRadius) : undefined,
      },
      onInit: (_: PayPalData, actions: PayPalInitActions) => {
        setPayPalActions(actions)
        setIsRendered(true)
      },
      createVaultSetupToken: async () => vaultSetupToken,
      onClick,
      onApprove,
      onCancel,
      onError,
    })

    setButton(buttonsInstance)
    buttonsInstance.render(`#${PAYPAL_CONTAINER_ID}`)
  }

  const destroyButton = () => {
    button?.close()
    setButton(undefined)
    setPayPalActions(undefined)
    setIsRendered(false)
  }

  return {
    isInitialized,
    isRendered,
    actions: payPalActions,
    initialize,
    renderButton,
    destroyButton,
    enable: () => payPalActions?.enable(),
    disable: () => payPalActions?.disable(),
  }
}
